import { useEffect } from 'react';

interface ResourceHintsProps {
  preloadImages?: string[];
  preconnectDomains?: string[];
  prefetchRoutes?: string[];
  preloadChunks?: string[];
}

export const ResourceHints: React.FC<ResourceHintsProps> = ({
  preloadImages = [],
  preconnectDomains = [],
  prefetchRoutes = [],
  preloadChunks = [],
}) => {
  useEffect(() => {
    const head = document.head;
    const addedElements: HTMLElement[] = [];

    // Preconnect to external domains
    preconnectDomains.forEach(domain => {
      const link = document.createElement('link');
      link.rel = 'preconnect';
      link.href = domain;
      link.crossOrigin = 'anonymous';
      head.appendChild(link);
      addedElements.push(link);
    });

    // Preload critical images
    preloadImages.forEach(imageSrc => {
      const link = document.createElement('link');
      link.rel = 'preload';
      link.href = imageSrc;
      link.as = 'image';
      
      // Add WebP support detection
      if (imageSrc.includes('.webp')) {
        link.type = 'image/webp';
      }
      
      head.appendChild(link);
      addedElements.push(link);
    });

    // Prefetch routes for faster navigation
    prefetchRoutes.forEach(route => {
      const link = document.createElement('link');
      link.rel = 'prefetch';
      link.href = route;
      head.appendChild(link);
      addedElements.push(link);
    });

    // Preload critical chunks
    preloadChunks.forEach(() => {
      // This would need to be implemented with actual chunk URLs
      // For now, we'll add DNS prefetch for faster loading
      const link = document.createElement('link');
      link.rel = 'dns-prefetch';
      link.href = window.location.origin;
      head.appendChild(link);
      addedElements.push(link);
    });

    // Cleanup function
    return () => {
      addedElements.forEach(element => {
        if (element.parentNode) {
          element.parentNode.removeChild(element);
        }
      });
    };
  }, [preloadImages, preconnectDomains, prefetchRoutes, preloadChunks]);

  return null; // This component doesn't render anything
};

export default ResourceHints;